import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import {
    convertToApiURL,
    convertToEssentialsListMapSearchApiURL,
    convertToSchoolMapSearchUrl,
    makeSearchURL,
} from '../common/searchRequestUtils.js';
import { RequestList } from './RequestList.js';
import { MapSearchInput } from './MapSearchInput.js';
import { NeedLevelLegend } from './NeedLevelLegend.js';
import * as MapSearchUtils from './MapSearchUtils.js';
import { Svg } from '../common/Svg.js';
import { MapSearchContext } from './MapSearchContext.js';
import { applyResultFilters, MIN_MARKERS_IN_VIEWPORT_FOR_SEARCH } from './MapSearchUtils.js';
import { DesktopMapSearchOptions, MobileMapSearchOptions } from './MapSearchOptions.js';
import { MapSearchHelper } from './MapSearchHelper.js';
import { LoadingIndicator } from './LoadingIndicator.js';
import { MapSearchInputHolder } from './MapSearchInputHolder.js';

const MAX_RESULTS_TO_DISPLAY = 60;
const MAX_RESULTS_TO_DISPLAY_FOR_MOBILE = 25;

/* global google getURLParams dcAnalytics */

function errorLoadingSearchResults(response) {
    console.log('Error loading search results', response);
    dcAnalytics.track('MapSearchResultError', response);
}
function getKeyFromLatLng(latitude, longitude) {
    return latitude.toString() + longitude.toString()
}

const shouldDrawMapMarkerForGeopoint = function (hasEssentialsResults, hasProjectResults, distanceFromSearchCenter, resultsBoundingRadius, displayEssentialsResultsOnly) {
    if (displayEssentialsResultsOnly && !hasEssentialsResults) {
        return false;
    }
    return distanceFromSearchCenter < resultsBoundingRadius;
}

const getMaxResults = function (isMobile) {
    return isMobile ? MAX_RESULTS_TO_DISPLAY_FOR_MOBILE : MAX_RESULTS_TO_DISPLAY;
};

// This is to reduce the likelihood that we are missing schools or essentials within the search radius of project results
// The project results are the "bounding circle" for the rest of the search results.
const getMaxResultsForSchoolOrEssentialsSearch = function (isMobile) {
    return getMaxResults(isMobile) * 2;
}

const constructHtmlStringForMarkerWithProjectResults = function(dataForMarker, isSelected, hasEssentials, hasMultipleRequests, displayOnlyClassroomEssentials) {
    const primaryNeedLevelForDisplay = dataForMarker.primaryNeedLevelForDisplay;
    const totalNumRequests = dataForMarker.totalNumRequests;
    const minCostToComplete = MapSearchUtils.Helpers.roundCostToCompleteForDisplay(dataForMarker.minCostToComplete).toLocaleString('en-US');
    const isSelectedSelector = isSelected ? ' selected-marker' : '';
    const isDisplayOnlyEssentialsSelector = displayOnlyClassroomEssentials ? ' classroom-essentials-only' : '';
    const countSelector = hasMultipleRequests ? ' multiple' : ' single';
    const hasMultipleSchools = dataForMarker.schoolsData.length > 1 ? 'multiple-schools' : '';

    let detailHtml = '';
    detailHtml += `
        <div class='school-entry ${dataForMarker.schoolsData[0].needLevelForDisplay}'>
            <div class='label'>
                <div class='top'>${!hasMultipleSchools ? dataForMarker.schoolsData[0].schoolName : (dataForMarker.schoolsData.length + ' schools')}</div>
        `;

    detailHtml += (totalNumRequests >= 0 ? `
                <div class='bottom'>${totalNumRequests} request${totalNumRequests > 1 ? 's' : ''}</div>
            </div>
        </div>
        ` : `
            </div>
        </div>
        `);

    let elementHtml =
        `<span tabindex='0' class='price-group ${primaryNeedLevelForDisplay}${isSelectedSelector}${isDisplayOnlyEssentialsSelector}${countSelector}'>
                <span class='price-marker school-name'>
                    ${detailHtml}
                </span>`;
    if (hasMultipleRequests) {
        elementHtml += `<span class='price-marker point-project-count ${primaryNeedLevelForDisplay}'>${totalNumRequests} request${totalNumRequests > 1 ? 's' : ''}</span>`;
    }

    elementHtml +=
            `<span class='price-marker first-project ${primaryNeedLevelForDisplay}'>
                <div class='school-marker-icon'></div>
            </span>
        </span>`;
    return elementHtml;
}

const constructHtmlStringForMarkerWithoutProjectResults = function(dataForMarker, isSelected) {
    const primaryNeedLevelForDisplay = dataForMarker.primaryNeedLevelForDisplay;
    const isSelectedSelector = isSelected ? ' selected-marker' : '';

    let school = dataForMarker.schoolsData[0];
    const hasMultipleSchools = dataForMarker.schoolsData.length > 1 ? 'multiple-schools' : '';

    return `<span tabindex='0' class='price-group no-requests ${primaryNeedLevelForDisplay} ${isSelectedSelector}'>
        <span class='price-marker school-name'>
            <div class='school-entry ${school.needLevelForDisplay}'>
                <div class="label">
                    <div class="top">
                        ${!hasMultipleSchools ? school.schoolName : (dataForMarker.schoolsData.length + ' schools')}
                    </div>
                </div>
            </div>
        </span>
        <div class='price-marker first-project ${primaryNeedLevelForDisplay}'>
            <div class='school-marker-icon'></div>
        </div>
    </span>
    `;
}

// This HTML will be passed directly to the Google Maps API Marker. It is not maintained by React.
const buildHtmlForMarker = function (marker, isSelected, displayOnlyClassroomEssentials) {
    const schoolNames = marker.schoolNames;
    const schoolsAtGeopoint = marker.schoolsAtGeopoint;
    const hasMultipleRequests = (marker.numProjectResults + marker.numEssentialsResults) > 0;
    const hasNoRequests = marker.numProjectResults == 0 && marker.numEssentialsResults == 0;
    const schoolsData = []
    let primaryNeedLevelForDisplay = null;
    let totalNumRequests = 0;
    let totalCostToComplete = 0;
    let minCostToComplete = marker.minCostToComplete;
    let hasEssentials = false;
    for (let index = 0; index < schoolNames.length; index++) {
        const schoolName = schoolNames[index];
        const schoolList = schoolsAtGeopoint[schoolName];
        const projects = schoolList['projects'];
        const essentials = schoolList['essentials']
        const school = schoolList['schools'][0]
        let freeReducedPriceLunch;
        hasEssentials = essentials.length > 0;

        if (!school || !school.hasOwnProperty('freeReducedPriceLunch')) {
            freeReducedPriceLunch = projects.length > 0 ? projects[0].freeReducedPriceLunch : essentials.length > 0 ? essentials[0].freeReducedPriceLunch : 0;
        } else {
            freeReducedPriceLunch = school.freeReducedPriceLunch
        }
        const needLevelForDisplay = MapSearchUtils.Helpers.getNeedLevelForDisplay(freeReducedPriceLunch);
        if (!primaryNeedLevelForDisplay) {
            primaryNeedLevelForDisplay = needLevelForDisplay;
        }
        const numRequests = schoolList['numRequests'];
        const schoolData = {
            needLevelForDisplay: needLevelForDisplay,
            numRequests: numRequests,
            schoolName: schoolName,
        }
        schoolsData.push(schoolData);
        totalNumRequests += numRequests;
        totalCostToComplete += schoolList['totalCostToComplete'];
    }
    const dataForMarker = {
        primaryNeedLevelForDisplay: primaryNeedLevelForDisplay,
        schoolsData: schoolsData,
        totalNumRequests: totalNumRequests,
        minCostToComplete: minCostToComplete,
    }
    if (hasNoRequests) {
        return constructHtmlStringForMarkerWithoutProjectResults(dataForMarker, isSelected).toString();
    } else {
        return constructHtmlStringForMarkerWithProjectResults(dataForMarker, isSelected, hasEssentials, hasMultipleRequests, displayOnlyClassroomEssentials).toString();
    }
};

const getDefaultSearchResult = function (isThisSearchEnabled) {
    return isThisSearchEnabled ? null : {length: 0, totalResults: 0}
}

const classNamesFromFilters = (filters) => {
    let result = '';

    // This could be done more cleanly with Object.entries and Array.reduce, but we'd have to write a camelCase to
    // kebab-case function that way.
    if (filters.lowCostOnly) {
        result += ' low-cost-only';
    }
    if (filters.equityFocus) {
        result += ' equity-focus';
    }
    if (filters.matchOffers) {
        result += ' match-offers';
    }

    return result;
}

const numOfFilters = (filters, displayOnlyClassroomEssentials) => {
    let n = 0;

    if (filters.lowCostOnly) {
        n++;
    }
    if (filters.equityFocus) {
        n++;
    }
    if (filters.matchOffers) {
        n++;
    }
    if (displayOnlyClassroomEssentials) {
        n++;
    }

    return n;
}

export const MapSearchResults = function ({ map, startingLat, startingLng, HtmlMapMarker, isMobile, getMapHeight, initialHeight, essentialsListEnabled: _essentialsListEnabled, schoolDonationEnabled, initialLocationSearchParams, parentModifierCallbacks, startingResultFilters, startingDisplayOnlyClassroomEssentials }) {
    const [searchParams, setSearchParams] = useState({});
    // Search filtering options that can be toggled from the map-search UI.
    //
    // Underscoring because we need to embed some business rules in the on-change behavior here.
    const [resultFilters, _setResultFilters] = useState(startingResultFilters);
    // Whether or not we should fetch/display essentials-lists. False if feature-flags say essentials-lists are off or
    // we're filtering on match-eligibility (which doesn't work with essentials-lists).
    //
    // Once essentials-list-search can filter by match-status, remove the prop-alias and remove this state-field.
    const [essentialsListEnabled, setEssentialsListEnabled] = useState(_essentialsListEnabled && !startingResultFilters.matchOffers);
    const [locationSearchParams, setLocationSearchParams] = useState(initialLocationSearchParams || null);

    const [projectSearchResult, setProjectSearchResult] = useState(null);
    const [classroomEssentialsSearchResult, setClassroomEssentialsSearchResult] = useState(getDefaultSearchResult(essentialsListEnabled));
    const [schoolSearchResult, setSchoolSearchResult] = useState(getDefaultSearchResult(schoolDonationEnabled));
    const [isLoadingProjectSearchResults, setIsLoadingProjectSearchResults] = useState(false);
    const [isLoadingEssentialsSearchResults, setIsLoadingEssentialsSearchResults] = useState(false);
    const [isLoadingSchoolSearchResults, setIsLoadingSchoolSearchResults] = useState(false);
    const [isLocationInputToggled, setLocationInputToggle] = useState(false);
    const [isSearchOptionsOpen, setSearchOptionsOpen] = useState(false);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [numClassroomEssentialsInSearchRadius, setNumClassroomEssentialsInSearchRadius] = useState(0);
    // Underscoring because we need to embed some business rules in the on-change behavior here.
    const [displayOnlyClassroomEssentials, _setDisplayOnlyClassroomEssentials] = useState(startingDisplayOnlyClassroomEssentials);
    const [showSearchBox, setSearchBox] = useState(true);

    // SearchCircle is a reference to the results circle visual aid rendered on the map. Kept in state to interact with.
    const [searchCircle, setSearchCircle] = useState(null);
    // MapListener is a reference to the callback function on the map when the map is dragged. Kept in state to interact with.
    const [mapListener, setMapListener] = useState(null);

    const [isInitialSearch, setIsInitialSearch] = useState(true);
    const [mapHeight, setMapHeight] = useState(initialHeight);
    const [searchResultBounds, setSearchResultBounds] = useState({});
    const [showNearbyProjectsHelper, setShowNearbyProjectsHelper] = useState(false);
    const [numMapDragSearchEvent, setNumMapDragSearchEvent] = useState(0);
    const [checkZoomLevel, setCheckZoomLevel] = useState(false);
    const locationSearchBoxRef = useRef(null);
    const prevMarkerRef = useRef();
    useEffect(() => {
        prevMarkerRef.current = selectedMarker;
    });
    const prevMarker = prevMarkerRef.current;

    // Re-search when resultFilters change.
    // We need this useRef to make sure the `useEffect` does nothing the first time it runs.
    const didMountRef = useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }

        performNewSearchOnMapCenter();
    }, [resultFilters]);

    const setResultFilters = (newResultFilters) => {
        if (newResultFilters.matchOffers) {
            if (displayOnlyClassroomEssentials) {
                _setDisplayOnlyClassroomEssentials(false);
            }
            // The essentials-list search index can't filter by match-status, so it'll return wrong results. Instead,
            // skip essentials-list-searching when this filter is on.
            setEssentialsListEnabled(false);
            // Also blow away our existing results.
            setClassroomEssentialsSearchResult([]);
        } else {
            // When turning *off* the match-offers filter, reset to starting state
            setEssentialsListEnabled(_essentialsListEnabled);
        }
        _setResultFilters(newResultFilters);
    };
    const setDisplayOnlyClassroomEssentials = (newValue) => {
        if (newValue && resultFilters.matchOffers) {
            const newResultFilters = { ...resultFilters };
            newResultFilters.matchOffers = false;
            setResultFilters(newResultFilters);
        }
        _setDisplayOnlyClassroomEssentials(newValue);
    };

    const clearSelectedMarker = function () {
        setShowNearbyProjectsHelper(false);
        setSelectedMarker(null);
        parentModifierCallbacks.setMinimizePoints(false);
    };

    const clearLocationSearchBox = function () {
        if (locationSearchBoxRef && locationSearchBoxRef.current) {
            locationSearchBoxRef.current.clearLocationSearchBox();
        }
    };

    // eslint-disable-next-line no-unused-vars
    const [movedFromCenter, _setMovedFromCenter] = useState(0);
    // Replacement setter function that can be used as a callback by the map.event
    const updateDistanceFromCenter = function () {
        if (isMobile) {
            clearSelectedMarker();
        }
        _setMovedFromCenter(movedFromCenter+1);
        const currentBounds = map.getBounds();
        const markers = searchMarkersRef.current;
        let numMarkersInViewport = 0;
        for (let i = 0; i < markers.length; i++) {
            if (markers[i] && markers[i].latlng && currentBounds.contains(markers[i].latlng)) {
                numMarkersInViewport++;
                if (numMarkersInViewport > MapSearchUtils.MIN_MARKERS_IN_VIEWPORT_FOR_TOOLTIP) {
                    setShowNearbyProjectsHelper(false);
                    return;
                }
            }
        }
        setShowNearbyProjectsHelper(numMarkersInViewport <= MapSearchUtils.MIN_MARKERS_IN_VIEWPORT_FOR_TOOLTIP);
    };

    const [searchMarkers, _setSearchMarkers] = useState([]);
    // This ref is used by the updateDistanceFromCenter callback method, because the callback is outside the scope of the React component
    const searchMarkersRef = useRef();
    searchMarkersRef.current = searchMarkers;
    // Create a custom setter function instead of using the React setter because there's clean-up steps for the old searchMarkers
    const setSearchMarkers = (newSearchMarkers) => {
        searchMarkers.forEach(marker => {
            marker.setMap(null);
        });
        newSearchMarkers.forEach(marker => {
            marker.setMap(map);
        });
        _setSearchMarkers(newSearchMarkers);
    };

    const updateMapViewportCenter = function () {
        if (searchParams && searchParams.centerLat && searchParams.centerLng) {
            const searchResultsCenter = new google.maps.LatLng(searchParams.centerLat, searchParams.centerLng);
            map.panTo(searchResultsCenter);
        }
        _setMovedFromCenter(0);
    };

    const shouldPerformSearchOnMapCenter = function () {
        if (numMapDragSearchEvent >= MapSearchUtils.MAX_AUTO_SEARCH_COUNT) {
            return false;
        }
        const center = map.getCenter();
        return MapSearchUtils.Helpers.calculateDistanceSquaredFromCenter(center.lat(), center.lng(), searchParams.centerLat, searchParams.centerLng) > searchResultBounds;
    }

    const displayNewResultsOnMapIfDoneLoading = function() {
        const doneLoading = !isLoadingProjectSearchResults && !isLoadingEssentialsSearchResults && !isLoadingSchoolSearchResults;
        if (doneLoading) {
            updateMapViewportCenter();
            setMapHeight(getMapHeight());
            if (projectSearchResult && (classroomEssentialsSearchResult || !essentialsListEnabled) && (schoolSearchResult || !schoolDonationEnabled)) {
                processSearchResults();
            }
            map.setOptions({ gestureHandling: 'greedy' });
        }
    };

    const toggleDisplayEssentialsOnly = function () {
        setSelectedMarker(null);
        setDisplayOnlyClassroomEssentials(!displayOnlyClassroomEssentials);
    }

    const prepareMapForNewSearch = function() {
        map.setOptions({ gestureHandling: 'none' });
        setIsLoadingProjectSearchResults(true);
        setIsLoadingEssentialsSearchResults(essentialsListEnabled);
        setIsLoadingSchoolSearchResults(schoolDonationEnabled);
        setProjectSearchResult(null);
        setClassroomEssentialsSearchResult(getDefaultSearchResult(essentialsListEnabled));
        setSchoolSearchResult(getDefaultSearchResult(schoolDonationEnabled));
        _setMovedFromCenter(0);
        clearSelectedMarker();

        // Close any "choose location" mobile lightboxes that are open
        setLocationInputToggle(false);
        // Close filter-toggle mobile lightbox
        setSearchOptionsOpen(false);
    }

    const context = { resultFilters, setResultFilters, toggleDisplayEssentialsOnly };

    const performNewSearchOnMapCenter = function () {
        prepareMapForNewSearch();
        const newSearchParams = MapSearchUtils.Helpers.getNewSearchParamsFromMapCenter(map, startingLat, startingLng);
        applyResultFilters(newSearchParams, context.resultFilters);
        const searchUrl = makeSearchURL(newSearchParams, MapSearchUtils.DEFAULT_PROJECT_SEARCH_URL, null);
        setSearchParams(newSearchParams);
        fetchProjectSearchResults(searchUrl);
        if (essentialsListEnabled) {
            fetchEssentialsSearchResults(searchUrl);
        }
        if (schoolDonationEnabled) {
            fetchSchoolSearchResults(searchUrl);
        }
    };

    const performNewLocationSuggestionSearch = function (locationSearchUrl) {
        prepareMapForNewSearch();

        const { newSearchParams, newLocationSearchParams } = MapSearchUtils.Helpers.getNewSearchParamsFromLocationSearchUrl(locationSearchUrl);
        applyResultFilters(newSearchParams, context.resultFilters);
        setSearchParams(newSearchParams);
        setLocationSearchParams(newLocationSearchParams);
        fetchProjectSearchResults(makeSearchURL(newSearchParams, MapSearchUtils.DEFAULT_PROJECT_SEARCH_URL, null));
        if (essentialsListEnabled) {
            fetchEssentialsSearchResults(makeSearchURL(newSearchParams, MapSearchUtils.DEFAULT_LOW_BARRIER_TO_ENTRY_SEARCH_URL, null));
        }
        if (schoolDonationEnabled) {
            fetchSchoolSearchResults(makeSearchURL(newSearchParams, MapSearchUtils.DEFAULT_SCHOOL_SEARCH_URL, null));
        }
    };

    const updateMapStateToDisplayNewSearchResults = function (markers, resultsBoundingRadius, geopointsWithEssentialsResults) {
        const newSearchCircle = MapSearchUtils.Helpers.getSearchCircle(resultsBoundingRadius, map);
        if (searchCircle) {
            searchCircle.setMap(null);
        }
        setSearchCircle(newSearchCircle);
        setSearchMarkers(markers);
        setSearchResultBounds(resultsBoundingRadius);
        setNumClassroomEssentialsInSearchRadius(geopointsWithEssentialsResults);

        if (isInitialSearch || locationSearchParams) {
            updateMapZoomToFitGeoMarkers(markers);
        }

        map.setOptions({ gestureHandling: 'greedy' });
    }

    const fetchProjectSearchResults = function (searchUrl) {
        const apiUrl = convertToApiURL(searchUrl, getMaxResults(), getMaxResults());
        $.ajax({
            url: apiUrl,
            dataType: 'json',
            success: newProjectSearchResult => {
                setIsLoadingProjectSearchResults(false);
                setProjectSearchResult(newProjectSearchResult);
            },
            error: response => {
                errorLoadingSearchResults(response);
                setIsLoadingProjectSearchResults(false);
                setProjectSearchResult(null);
            },
        });
    };

    const fetchEssentialsSearchResults = function(searchUrl) {
        const essentialsMapSearchUrl = convertToEssentialsListMapSearchApiURL(searchUrl, getMaxResultsForSchoolOrEssentialsSearch());
        $.ajax({
            url: essentialsMapSearchUrl,
            dataType: 'json',
            success: searchResult => {
                setIsLoadingEssentialsSearchResults(false);
                setClassroomEssentialsSearchResult(searchResult);
            },
            error: response => {
                errorLoadingSearchResults(response);
                setIsLoadingEssentialsSearchResults(false);
                setClassroomEssentialsSearchResult(getDefaultSearchResult(essentialsListEnabled));
            },
        });
    }

    const fetchSchoolSearchResults = function(searchUrl) {
        const schoolMapSearchUrl = convertToSchoolMapSearchUrl(searchUrl, getMaxResultsForSchoolOrEssentialsSearch());
        $.ajax({
            url: schoolMapSearchUrl,
            dataType: 'json',
            success: searchResult => {
                setIsLoadingSchoolSearchResults(false);
                setSchoolSearchResult(searchResult);
            },
            error: response => {
                errorLoadingSearchResults(response);
                setIsLoadingSchoolSearchResults(false);
                setSchoolSearchResult(getDefaultSearchResult(schoolDonationEnabled));
            },
        });
    }

    useEffect(() => {
        displayNewResultsOnMapIfDoneLoading();
    }, [isLoadingEssentialsSearchResults, isLoadingProjectSearchResults, isLoadingSchoolSearchResults, projectSearchResult, classroomEssentialsSearchResult, schoolSearchResult])

    // This only considers the farthest ProposalSearchResult (which is the last projectSearchResult, because results are geosorted by distance.
    // If less than the max number of search results, then we have all the projects within DEFAULT_SEARCH_RADIUS, because the search was exhaustive.
    // Otherwise, calculate out the approximate distance from the center of the last project (results are geo-sorted, so just calculate the last result)
    // We do not show LBE results outside this circle.
    const calculateBoundingRadiusOfResults = function(projectSearchResult) {
        if (projectSearchResult.proposals.length < getMaxResults()) {
            return MapSearchUtils.DEFAULT_SEARCH_RADIUS_IN_KM;
        } else {
            const furthestProject = projectSearchResult.proposals[projectSearchResult.proposals.length - 1];
            return MapSearchUtils.Helpers.calculateDistanceSquaredFromCenter(furthestProject.latitude, furthestProject.longitude, searchParams.centerLat, searchParams.centerLng);
        }
    }

    const processSearchResultsIntoGeopoints = function(projectResults, essentialsResults, schoolSearchResults) {
        const geopoints = {};
        for (let i = 0; projectResults.hasOwnProperty('proposals') && i < projectResults.proposals.length; i++) {
            const _proposal = projectResults.proposals[i];
            const key = getKeyFromLatLng(_proposal.latitude, _proposal.longitude);
            if (Object.prototype.hasOwnProperty.call(geopoints, key)) {
                geopoints[key]['project'].push(_proposal);
            } else {
                geopoints[key] = {};
                geopoints[key]['project'] = [_proposal];
                geopoints[key]['schools'] = [];
                geopoints[key]['essentials'] = [];
            }
        }
        for (let i = 0; essentialsResults.hasOwnProperty('essentialsLists') && i < essentialsResults.essentialsLists.length; i++) {
            const _classroomEssentials = essentialsResults.essentialsLists[i];
            const key = getKeyFromLatLng(_classroomEssentials.latitude, _classroomEssentials.longitude);
            if (Object.prototype.hasOwnProperty.call(geopoints, key)) {
                geopoints[key]['essentials'].push(_classroomEssentials);
            } else {
                geopoints[key] = {};
                geopoints[key]['project'] = [];
                geopoints[key]['schools'] = [];
                geopoints[key]['essentials'] = [_classroomEssentials];
            }
        }
        for (let i = 0; schoolSearchResults.hasOwnProperty('length') && i < schoolSearchResults.length; i++) {
            const _school = schoolSearchResults[i];
            const key = getKeyFromLatLng(_school.latitude, _school.longitude);
            if (Object.prototype.hasOwnProperty.call(geopoints, key)) {
                geopoints[key]['schools'].push(_school);
            } else {
                geopoints[key] = {};
                geopoints[key]['project'] = [];
                geopoints[key]['essentials'] = [];
                geopoints[key]['schools'] = [_school];
            }
        }
        return geopoints;
    }

    const processGeopointIntoMapMarker = function(geopointResults, resultsBoundingRadius) {
        const projectResults = geopointResults[1]['project'];
        const essentialsResults = geopointResults[1]['essentials'];
        const schoolResults = geopointResults[1]['schools'];
        const hasProjectResults = projectResults.length > 0;
        const hasEssentialsResults = essentialsResults.length > 0;
        let firstResult;
        if (hasProjectResults) {
            firstResult = projectResults[0];
        } else if (hasEssentialsResults) {
            firstResult = essentialsResults[0];
        } else {
            firstResult = schoolResults[0];
        }
        const latitude = Number(firstResult.latitude);
        const longitude = Number(firstResult.longitude);
        const distanceFromSearchCenter = MapSearchUtils.Helpers.calculateDistanceSquaredFromCenter(latitude, longitude, searchParams.centerLat, searchParams.centerLng);
        const latLong = { lat: latitude, lng: longitude };
        schoolResults.sort(MapSearchUtils.Helpers.sortByFrplAndSchoolNameAndCostToComplete);
        projectResults.sort(MapSearchUtils.Helpers.sortByFrplAndSchoolNameAndCostToComplete);
        essentialsResults.sort(MapSearchUtils.Helpers.sortByFrplAndSchoolNameAndCostToComplete);
        const schoolsAtGeopoint = {};
        const schoolNames = [];
        let minCostToComplete = Number.MAX_SAFE_INTEGER;
        let numEssentialsResults = 0;
        for (const _school of schoolResults) {
            const schoolName = _school.schoolName;
            if (schoolsAtGeopoint.hasOwnProperty(schoolName)) {
                schoolsAtGeopoint[schoolName]['schools'].push(_school);
            } else {
                schoolNames.push(schoolName);
                schoolsAtGeopoint[schoolName] = {};
                schoolsAtGeopoint[schoolName]['schools'] = [_school];
                schoolsAtGeopoint[schoolName]['essentials'] = [];
                schoolsAtGeopoint[schoolName]['projects'] = [];
                schoolsAtGeopoint[schoolName]['numRequests'] = 0;
                schoolsAtGeopoint[schoolName]['totalCostToComplete'] = Number(0);
                schoolsAtGeopoint[schoolName]['minCostToComplete'] = Number.MAX_SAFE_INTEGER;
            }
        }
        for (const _project of projectResults) {
            const schoolName = _project.schoolName
            if (schoolsAtGeopoint.hasOwnProperty(schoolName)) {
                schoolsAtGeopoint[schoolName]['projects'].push(_project);
                schoolsAtGeopoint[schoolName]['numRequests'] += 1
                schoolsAtGeopoint[schoolName]['totalCostToComplete'] += Number(_project.costToComplete);
                schoolsAtGeopoint[schoolName]['minCostToComplete'] = Math.min(Number(_project.costToComplete), schoolsAtGeopoint[schoolName]['minCostToComplete']);
                minCostToComplete = Math.min(Number(_project.costToComplete), minCostToComplete);
            } else {
                schoolNames.push(schoolName);
                schoolsAtGeopoint[schoolName] = {};
                schoolsAtGeopoint[schoolName]['essentials'] = [];
                schoolsAtGeopoint[schoolName]['projects'] = [_project];
                schoolsAtGeopoint[schoolName]['schools'] = [];
                schoolsAtGeopoint[schoolName]['numRequests'] = 1;
                schoolsAtGeopoint[schoolName]['totalCostToComplete'] = Number(_project.costToComplete);
                schoolsAtGeopoint[schoolName]['minCostToComplete'] = Number(_project.costToComplete);
                minCostToComplete = Math.min(Number(_project.costToComplete), minCostToComplete);
            }
        }
        for (let _essentials of essentialsResults) {
            if (_essentials.hideTeacherDetails) {
                continue;
            }
            numEssentialsResults += 1;
            const schoolName = _essentials.schoolName
            if (schoolsAtGeopoint.hasOwnProperty(schoolName)) {
                schoolsAtGeopoint[schoolName]['essentials'].push(_essentials);
                schoolsAtGeopoint[schoolName]['numRequests'] += 1;
                schoolsAtGeopoint[schoolName]['totalCostToComplete'] += Number(_essentials.topItem.price);
                schoolsAtGeopoint[schoolName]['minCostToComplete'] = Math.min(Number(_essentials.topItem.price), schoolsAtGeopoint[schoolName]['minCostToComplete']);
                minCostToComplete = Math.min(Number(_essentials.topItem.price), minCostToComplete);
            } else {
                schoolNames.push(schoolName);
                schoolsAtGeopoint[schoolName] = {};
                schoolsAtGeopoint[schoolName]['essentials'] = [_essentials];
                schoolsAtGeopoint[schoolName]['projects'] = [];
                schoolsAtGeopoint[schoolName]['schools'] = [];
                schoolsAtGeopoint[schoolName]['numRequests'] = 1;
                schoolsAtGeopoint[schoolName]['totalCostToComplete'] = Number(_essentials.topItem.price);
                schoolsAtGeopoint[schoolName]['minCostToComplete'] = Math.min(Number(_essentials.topItem.price));
                minCostToComplete = Math.min(Number(_essentials.topItem.price), minCostToComplete);
            }
        }
        const drawMapMarkerForGeopoint = shouldDrawMapMarkerForGeopoint(hasEssentialsResults, hasProjectResults, distanceFromSearchCenter, resultsBoundingRadius, false);
        if (!drawMapMarkerForGeopoint) {
            return [];
        }
        // We've flipped on using multiple points vs single points to display results about 4 times now,
        // I'm just going to keep this commented out instead of deleting it
//        const geopointsForSchools = MapSearchUtils.Helpers.getPointsAroundCenter(latLong, schoolNames.length, MapSearchUtils.DEFAULT_GEOPOINT_SPREAD_RADIUS);
//        let markersForGeopoint = [];
//        let index = 0;
        // To draw schools in individual points, split them into their own marker
//        for (const _schoolName of schoolNames) {
//            schoolsAtGeopoint[_schoolName]['essentials'].sort(MapSearchUtils.Helpers.sortEssentialsByTopItemCost);
//            schoolsAtGeopoint[_schoolName]['projects'].sort(MapSearchUtils.Helpers.sortProjectsByCostToComplete);
//            const schoolResult = schoolsAtGeopoint[_schoolName];
//            const schoolListPlaceholder = {};
//            schoolListPlaceholder[_schoolName] = {}
//            schoolListPlaceholder[_schoolName]['schools'] = schoolsAtGeopoint[_schoolName]['schools'];
//            schoolListPlaceholder[_schoolName]['essentials'] = schoolsAtGeopoint[_schoolName]['essentials'];
//            schoolListPlaceholder[_schoolName]['projects'] = schoolsAtGeopoint[_schoolName]['projects'];
//            schoolListPlaceholder[_schoolName]['numRequests'] = schoolsAtGeopoint[_schoolName]['numRequests'];
//            schoolListPlaceholder[_schoolName]['totalCostToComplete'] = schoolsAtGeopoint[_schoolName]['totalCostToComplete'];
//            schoolListPlaceholder[_schoolName]['minCostToComplete'] = schoolsAtGeopoint[_schoolName]['minCostToComplete'];
//            const schoolNamesPlaceholder = [_schoolName];
//            const marker = createMarkerForResults(
//                geopointsForSchools[index],
//                schoolListPlaceholder,
//                schoolNamesPlaceholder,
//                schoolResult['projects'].length,
//                schoolResult['essentials'].length,
//                distanceFromSearchCenter,
//                schoolsAtGeopoint[_schoolName]['minCostToComplete'],
//            );
//            markersForGeopoint.push(marker);
//            index++;
//        }
        for (const _schoolName of schoolNames) {
            schoolsAtGeopoint[_schoolName]['essentials'].sort(MapSearchUtils.Helpers.sortEssentialsByTopItemCost);
            schoolsAtGeopoint[_schoolName]['projects'].sort(MapSearchUtils.Helpers.sortProjectsByCostToComplete);
        }
        const marker = createMarkerForResults(latLong, schoolsAtGeopoint, schoolNames, projectResults.length, numEssentialsResults, distanceFromSearchCenter, minCostToComplete);
        return [marker];
    }

    const processSearchResults = function () {
        const geopoints = processSearchResultsIntoGeopoints(projectSearchResult, classroomEssentialsSearchResult, schoolSearchResult);
        const markers = [];
        const resultsBoundingRadius = calculateBoundingRadiusOfResults(projectSearchResult);
        let geopointsWithEssentialsResults = 0;
        for (const _results of Object.entries(geopoints)) {
            const markersForGeopoint = processGeopointIntoMapMarker(_results, resultsBoundingRadius);
            for(const _marker of markersForGeopoint) {
                if (_marker) {
                    if (_marker.numEssentialsResults > 0) {
                        geopointsWithEssentialsResults += 1;
                    }
                    markers.push(_marker);
                }
            }
        }
        updateMapStateToDisplayNewSearchResults(markers, resultsBoundingRadius, geopointsWithEssentialsResults);
        if (markers) {
            const numMarkersInViewport = getNumberOfMarkersInSearchBounds();
            trackMapSearchEvent(numMarkersInViewport);
        }
    };

    const createMarkerForResults = function (latLong, schoolsAtGeopoint, schoolNames, numProjectResults, numEssentialsResults, distanceFromSearchCenter, minCostToComplete) {
        const marker = new HtmlMapMarker({
            latlng: latLong,
            distanceFromSearchCenter: distanceFromSearchCenter,
            numEssentialsResults: numEssentialsResults,
            numProjectResults: numProjectResults,
            schoolNames: schoolNames,
            schoolsAtGeopoint: schoolsAtGeopoint,
            isSelected: false,
            minCostToComplete: minCostToComplete,
        });
        marker.html = buildHtmlForMarker(marker, false, false );
        marker.addListener('click', function () {
            parentModifierCallbacks.setMinimizePoints(true);
            setSelectedMarker(this);
            const markerLatLng = this.getPosition();
            if (isMobile || !markerLatLng) {
                return;
            }
            // Recenter the map to the marker when clicked, if the marker is currently on the far-right side of the map.
            const mapBounds = map.getBounds();
            const mapNorthEast = mapBounds.getNorthEast();
            const mapSouthWest = mapBounds.getSouthWest();
            const minLng = mapSouthWest.lng();
            const maxLng = mapNorthEast.lng();
            if ((markerLatLng.lng - minLng) / (maxLng - minLng) > MapSearchUtils.BOUNDS_RANGE_TO_RECENTER_TO_MARKER) {
                map.panTo(markerLatLng);
            }
        }.bind(marker));
        return marker;
    };

    const updateMapZoomToFitGeoMarkers = function (markers) {
        if (map.getZoom() <= MapSearchUtils.MIN_ZOOM) {
            return;
        }
        const numMarkers = markers.length;
        let desiredCountInViewport = Math.floor(numMarkers * MapSearchUtils.DEFAULT_MIN_PERCENTAGE_TO_DISPLAY_ON_INITIAL_SEARCH);
        desiredCountInViewport = desiredCountInViewport < MapSearchUtils.MIN_MARKERS_IN_VIEWPORT_FOR_SEARCH ? MapSearchUtils.MIN_MARKERS_IN_VIEWPORT_FOR_SEARCH : desiredCountInViewport;

        setShowNearbyProjectsHelper(false);
        let numMarkersInViewport = 0;
        if (!map.getBounds()) {
            // map hasn't finished loading - wait an arbitrary amount of time (200ms) before trying again
            console.debug('map not done loading');
            setTimeout(() => {
                setCheckZoomLevel(true);
                console.debug(checkZoomLevel);
            }, 200);
            return;
        }

        for (const marker of markers) {
            if (map && map.getBounds().contains(marker.getPosition())) {
                numMarkersInViewport++;
                if (numMarkersInViewport >= desiredCountInViewport) {
                    return;
                }
            }
        }
        
        if (numMarkersInViewport < desiredCountInViewport) {
            map.setZoom(map.getZoom() - 1);
            updateMapZoomToFitGeoMarkers(markers);
        }
    };

    const getNumberOfMarkersInSearchBounds = function () {
        let numMarkersInViewport = 0;
        if (!map.getBounds()) {
            // map hasn't finished loading - return early
            return searchMarkers.length;
        }
        for (const marker of searchMarkers) {
            if (map.getBounds().contains(marker.getPosition())) {
                numMarkersInViewport++;
            }
        }
        if (searchMarkers.length === 0) {
            setShowNearbyProjectsHelper(false);
        }
        return numMarkersInViewport;
    };

    const trackMapSearchEvent = function (numMarkersInViewport) {
        const eventData = {
            numMarkersInViewport: numMarkersInViewport,
            numMarkersInResult: searchMarkers ? searchMarkers.length : 0,
            initialSearch: isInitialSearch,
            zoomLevel: map.getZoom(),
            numMapDragSearchEvent: numMapDragSearchEvent,
        };
        if (locationSearchParams) {
            for (const paramName of Object.keys(locationSearchParams)) {
                eventData[paramName] = locationSearchParams[paramName];
            }
        } else {
            eventData.startingLat = startingLat;
            eventData.startingLng = startingLng;
        }
        dcAnalytics.track('MapSearchResult', eventData);
        if (projectSearchResult) {
            setIsInitialSearch(false);
        }
    };

    //for upcoming ui refactor for search filters
    const getMapInterface = function() {
        const filterClassNames = classNamesFromFilters(resultFilters);

        const showZoomOutContainer = (!isLoadingProjectSearchResults || !isLoadingEssentialsSearchResults || !isLoadingSchoolSearchResults) && showNearbyProjectsHelper;
        return (
            <div id='map-sidebar' className={((displayOnlyClassroomEssentials) ? 'classroom-essentials-only' : '') + filterClassNames}>
                {!selectedMarker && <h3 className="desktop">Search by location</h3>}
                {showZoomOutContainer && <div className="zoom-out-notification-container">
                    <MapSearchHelper
                        showNearbyProjectsHelper={showNearbyProjectsHelper}
                        performNewSearchOnMapCenter={performNewSearchOnMapCenter}
                        isLoading={isLoadingProjectSearchResults || isLoadingEssentialsSearchResults || isLoadingSchoolSearchResults}
                    />
                    <LoadingIndicator
                        isLoading={isLoadingProjectSearchResults || isLoadingEssentialsSearchResults || isLoadingSchoolSearchResults}
                    />
                </div> }
                <NeedLevelLegend/>
                {!selectedMarker &&
                    <div className="location-info">
                        <MobileMapSearchOptions
                            filterCount={numOfFilters(resultFilters, displayOnlyClassroomEssentials)}
                            isMobile={isMobile}
                            selectedMarker={selectedMarker}
                            isSearchOptionsOpen={isSearchOptionsOpen}
                            setSearchOptionsOpen={setSearchOptionsOpen}
                        />

                        <div className="mobile-secondary-actions">
                            <a className="button-secondary small" href={baseURL + '/donors/search.html'}>View list of projects</a>
                            <MapSearchInputHolder
                                isLocationInputToggled={isLocationInputToggled}
                                isMobile={isMobile}
                                setLocationInputToggle={setLocationInputToggle}
                                selectedMarker={selectedMarker}
                                projectSearchResult={projectSearchResult}
                                classroomEssentialsSearchResult={classroomEssentialsSearchResult}
                                searchMarkers={searchMarkers}
                                showNearbyProjectsHelper={showNearbyProjectsHelper}
                                locationSearchBoxRef={locationSearchBoxRef}
                                performNewLocationSuggestionSearch={performNewLocationSuggestionSearch}
                                isLoadingProjectSearchResults={isLoadingProjectSearchResults}
                                isLoadingEssentialsSearchResults={isLoadingEssentialsSearchResults}
                                isLoadingSchoolSearchResults={isLoadingSchoolSearchResults}
                                performNewSearchOnMapCenter={performNewSearchOnMapCenter}
                                numClassroomEssentialsInSearchRadius={numClassroomEssentialsInSearchRadius}
                                showSearchBox={showSearchBox}
                                setSearchBox={setSearchBox}
                                searchParams={searchParams}
                                locationSearchParams={locationSearchParams}
                            />
                        </div>
                    </div>}
                <RequestList
                    clearSelectedResults={clearSelectedMarker}
                    selectedMarker={selectedMarker}
                    isMobile={isMobile}
                    schoolDonationEnabled={schoolDonationEnabled}
                />

                <DesktopMapSearchOptions selectedMarker={selectedMarker} isMobile={isMobile} />
            </div>
        );
    };

    useEffect(() => {
        if (!isLoadingProjectSearchResults && !isLoadingEssentialsSearchResults && !isLoadingSchoolSearchResults) {
            performNewSearchOnMapCenter();
        }
    }, []);

    useEffect(() => {
        if (movedFromCenter === 0) {
            setMapListener(map.addListener('dragend', updateDistanceFromCenter));
        } else if (mapListener) {
            if (isMobile) {
                setSearchBox(false)
            }
            clearLocationSearchBox();
            mapListener.remove();
            setMapListener(null);
            if (shouldPerformSearchOnMapCenter()) {
                setNumMapDragSearchEvent(numMapDragSearchEvent+1);
                performNewSearchOnMapCenter();
            } else {
                setMapListener(map.addListener('dragend', updateDistanceFromCenter));
            }
        }
    }, [movedFromCenter]);

    useEffect(() => {
        map.addListener('dragstart', function() {
            setSelectedMarker(null);
            parentModifierCallbacks.setMinimizePoints(false);
            parentModifierCallbacks.togglePickedUp();
        });
        map.addListener('dragend', function() {
            parentModifierCallbacks.togglePickedUp();
        });
    }, []);

    useEffect(() => {
        if (prevMarker) {
            prevMarker.isSelected = false;
            if (!displayOnlyClassroomEssentials || prevMarker.numEssentialsResults > 0) {
                prevMarker.html = buildHtmlForMarker(prevMarker, false, displayOnlyClassroomEssentials);
                prevMarker.update();
            }
        }
        // TODO does this behavior need to be hidden behind the flag? as-is, it always runs
        parentModifierCallbacks.setPointOpen(!!selectedMarker);
        parentModifierCallbacks.setFullscreen(selectedMarker && selectedMarker.numProjectResults > 1);

        if (selectedMarker) {
            selectedMarker.isSelected = true;
            selectedMarker.html = buildHtmlForMarker(selectedMarker, true, displayOnlyClassroomEssentials);
            selectedMarker.update();
        }
    }, [selectedMarker]);

    useEffect(() => {
        for(let i = 0; i < searchMarkers.length; i++) {
            const marker = searchMarkers[i];
            const distanceFromSearchCenter = marker.distanceFromSearchCenter;
            const drawMapMarkerForGeopoint = shouldDrawMapMarkerForGeopoint(marker.numEssentialsResults > 0, marker.numProjectResults > 0, distanceFromSearchCenter, searchResultBounds, displayOnlyClassroomEssentials);
            if (!drawMapMarkerForGeopoint) {
                marker.html = `<span></span>`.toString();
            } else {
                marker.html = buildHtmlForMarker(marker, marker.isSelected, displayOnlyClassroomEssentials);
            }
            marker.update();
        }
    }, [displayOnlyClassroomEssentials, searchMarkers])

    useEffect(() => {
        if (checkZoomLevel && searchMarkers && searchMarkers.length) {
            updateMapZoomToFitGeoMarkers(searchMarkers);
            setCheckZoomLevel(false);
        }
    }, [checkZoomLevel]);

    return (
        <MapSearchContext.Provider value={context}>
            {getMapInterface()}
        </MapSearchContext.Provider>
    )
};

MapSearchResults.propTypes = {
    map: PropTypes.object.isRequired,
    drawingManager: PropTypes.object.isRequired,
    startingLat: PropTypes.Number,
    startingLng: PropTypes.Number,
    HtmlMapMarker: PropTypes.object,
    isMobile: PropTypes.bool,
    getMapHeight: PropTypes.func,
    initialHeight: PropTypes.string,
    essentialsListEnabled: PropTypes.bool,
    schoolDonationEnabled: PropTypes.bool,
    initialLocationSearchParams: PropTypes.object,
    // A set of functions for modifying the React tree's parent in response to React state changes.
    parentModifierCallbacks: PropTypes.object,
    // Initial values for the "result filters" state fields. Derived from query string.
    startingResultFilters: PropTypes.object,
    // Initial value for `displayOnlyClassroomEssentials`, a state-field controlling whether or not we should only show
    // classroom essentials results. Derived from query string.
    startingDisplayOnlyClassroomEssentials: PropTypes.bool.isRequired,
};
